import axios from 'axios';
import { API_BASE_URL } from '@config';
import * as ActionsDialog from 'app/store/actions';
import { showMessage } from 'app/store/actions/fuse';

// Action Types
export const SET_COST_REPORTS_LIST = 'COST_REPORTS/SET_LIST';
export const SET_COST_REPORT = 'COST_REPORTS/SET_REPORT';
export const SET_COST_REPORTS_LOADING = 'COST_REPORTS/SET_LOADING';
export const SET_COST_REPORTS_ERROR = 'COST_REPORTS/SET_ERROR';
export const SET_AVAILABLE_CREDENTIALS = 'COST_REPORTS/SET_AVAILABLE_CREDENTIALS';
export const SET_SELECTED_PROVIDER = 'COST_REPORTS/SET_SELECTED_PROVIDER';
export const SET_GENERATING_REPORT = 'COST_REPORTS/SET_GENERATING_REPORT';
export const ADD_GENERATING_REPORT = 'COST_REPORTS/ADD_GENERATING_REPORT';
export const REMOVE_GENERATING_REPORT = 'COST_REPORTS/REMOVE_GENERATING_REPORT';
export const CLEAR_COST_REPORT = 'COST_REPORTS/CLEAR_REPORT';
export const CLEAR_COST_REPORTS_ERROR = 'COST_REPORTS/CLEAR_ERROR';
export const SET_PAGINATION = 'COST_REPORTS/SET_PAGINATION';

// Cleanup action types
export const CLEANUP_START = 'COST_REPORTS/CLEANUP_START';
export const CLEANUP_PROGRESS = 'COST_REPORTS/CLEANUP_PROGRESS';
export const CLEANUP_RESOURCE_RESULT = 'COST_REPORTS/CLEANUP_RESOURCE_RESULT';
export const CLEANUP_COMPLETE = 'COST_REPORTS/CLEANUP_COMPLETE';
export const CLEANUP_RESET = 'COST_REPORTS/CLEANUP_RESET';

// Action Creators
export const setReportsList = reports => ({
	type: SET_COST_REPORTS_LIST,
	payload: reports
});

export const setReport = report => ({
	type: SET_COST_REPORT,
	payload: report
});

export const setLoading = loading => ({
	type: SET_COST_REPORTS_LOADING,
	payload: loading
});

export const setError = error => ({
	type: SET_COST_REPORTS_ERROR,
	payload: error
});

export const setAvailableCredentials = credentials => ({
	type: SET_AVAILABLE_CREDENTIALS,
	payload: credentials
});

export const setSelectedProvider = provider => ({
	type: SET_SELECTED_PROVIDER,
	payload: provider
});

export const setGeneratingReport = reportId => ({
	type: SET_GENERATING_REPORT,
	payload: reportId
});

export const addGeneratingReport = (reportData) => ({
	type: ADD_GENERATING_REPORT,
	payload: reportData
});

export const removeGeneratingReport = (reportId) => ({
	type: REMOVE_GENERATING_REPORT,
	payload: reportId
});

export const clearReport = () => ({
	type: CLEAR_COST_REPORT
});

export const clearError = () => ({
	type: CLEAR_COST_REPORTS_ERROR
});

export const setPagination = pagination => ({
	type: SET_PAGINATION,
	payload: pagination
});

// Headers for admin requests
const headers = {
	headers: {
		admin: true
	}
};

/**
 * Get all cost reports
 */
export const getCostReports = (page = 1, limit = 20, filters = {}) => dispatch => {
	dispatch(setLoading(true));
	
	let url = `${API_BASE_URL}/api/cost-reports?page=${page}&limit=${limit}`;
	if (filters.status) url += `&status=${filters.status}`;
	if (filters.provider) url += `&provider=${filters.provider}`;

	const request = axios.get(url, headers);
	
	return request
		.then(response => {
			dispatch(setReportsList(response.data.reports));
			dispatch(setPagination(response.data.pagination));
			dispatch(setLoading(false));
		})
		.catch(function(err) {
			var errData = err.response && err.response.data ? err.response.data : { message: 'Failed to fetch reports' };
			dispatch(setError(errData));
			dispatch(setLoading(false));
		});
};

/**
 * Get a single cost report by ID
 */
export const getCostReport = (reportId) => dispatch => {
	dispatch(setLoading(true));
	
	const request = axios.get(`${API_BASE_URL}/api/cost-reports/${reportId}`, headers);
	
	return request
		.then(response => {
			dispatch(setReport(response.data));
			dispatch(setLoading(false));
		})
		.catch(function(err) {
			var errData = err.response && err.response.data ? err.response.data : { message: 'Failed to fetch report' };
			dispatch(setError(errData));
			dispatch(setLoading(false));
		});
};

/**
 * Get available credentials for cost research
 */
export const getAvailableCredentials = () => dispatch => {
	const request = axios.get(`${API_BASE_URL}/api/cost-reports/available-credentials`, headers);
	
	return request
		.then(response => {
			dispatch(setAvailableCredentials(response.data));
		})
		.catch(err => {
			console.error('Failed to fetch available credentials:', err);
		});
};

/**
 * Generate a new cost report
 */
export const generateCostReport = (credentialId, periodDays, manualSavingsAdjustment) => (dispatch, getState) => {
	periodDays = periodDays || 30;
	manualSavingsAdjustment = manualSavingsAdjustment || 0;
	
	dispatch(ActionsDialog.toggleLoading());
	
	// Find credential name from available credentials
	var state = getState();
	var costReportsState = state.costReports && state.costReports.costReports ? state.costReports.costReports : {};
	var credentials = costReportsState.availableCredentials || [];
	var selectedCred = credentials.find(function(c) { return (c._id || c.id) === credentialId; });
	var actualCredName = selectedCred ? selectedCred.name : 'AWS Credential';
	
	var request = axios.post(
		API_BASE_URL + '/api/cost-reports/generate',
		{ 
			credentialId: credentialId, 
			periodDays: periodDays,
			manualSavingsAdjustment: manualSavingsAdjustment
		},
		headers
	);
	
	return request
		.then(function(response) {
			var reportId = response.data.reportId;
			
			// Add to persisted generating reports
			dispatch(addGeneratingReport({
				_id: reportId,
				credentialName: actualCredName,
				createdAt: new Date().toISOString(),
				periodDays: periodDays,
				status: 'generating'
			}));
			
			dispatch(setGeneratingReport(reportId));
			dispatch(ActionsDialog.toggleLoading());
			dispatch(ActionsDialog.closeDialog());
			dispatch(
				showMessage({
					message: 'Cost analysis started. This may take a few minutes.',
					variant: 'success'
				})
			);
			// Refresh list to show the new pending report
			dispatch(getCostReports());
			// Start polling for status
			dispatch(pollReportStatus(reportId));
			return response.data;
		})
		.catch(function(err) {
			dispatch(ActionsDialog.toggleLoading());
			var errData = err.response && err.response.data ? err.response.data : { message: 'Failed to generate report' };
			dispatch(setError(errData));
			var errMessage = err.response && err.response.data && err.response.data.message 
				? err.response.data.message 
				: 'Failed to start cost analysis';
			dispatch(
				showMessage({
					message: errMessage,
					variant: 'error'
				})
			);
		});
};

/**
 * Poll for report status until completed or failed
 */
export const pollReportStatus = (reportId, maxAttempts = 60) => dispatch => {
	let attempts = 0;
	
	const poll = () => {
		if (attempts >= maxAttempts) {
			dispatch(setGeneratingReport(null));
			dispatch(removeGeneratingReport(reportId));
			dispatch(
				showMessage({
					message: 'Cost analysis timed out. Please check the reports list.',
					variant: 'warning'
				})
			);
			return;
		}
		
		attempts++;
		
		axios.get(`${API_BASE_URL}/api/cost-reports/${reportId}/status`, headers)
			.then(response => {
				if (response.data.status === 'completed') {
					dispatch(setGeneratingReport(null));
					dispatch(removeGeneratingReport(reportId));
					dispatch(getCostReports());
					dispatch(
						showMessage({
							message: 'Cost analysis completed!',
							variant: 'success'
						})
					);
				} else if (response.data.status === 'failed') {
					dispatch(setGeneratingReport(null));
					dispatch(removeGeneratingReport(reportId));
					dispatch(getCostReports());
					dispatch(
						showMessage({
							message: 'Cost analysis failed. Check the report for details.',
							variant: 'error'
						})
					);
				} else {
					// Still generating, poll again in 5 seconds
					setTimeout(poll, 5000);
				}
			})
			.catch(() => {
				// Error polling, try again
				setTimeout(poll, 5000);
			});
	};
	
	// Start polling after 3 seconds
	setTimeout(poll, 3000);
};

/**
 * Resume polling for any persisted generating reports after page refresh
 */
export const resumeGeneratingReports = () => (dispatch, getState) => {
	var state = getState();
	var generatingReports = state.costReports && state.costReports.generatingReports 
		? state.costReports.generatingReports 
		: [];
	
	if (generatingReports.length === 0) {
		return;
	}
	
	// For each persisted generating report, check its current status
	generatingReports.forEach(function(report) {
		axios.get(API_BASE_URL + '/api/cost-reports/' + report._id + '/status', headers)
			.then(function(response) {
				if (response.data.status === 'completed' || response.data.status === 'failed') {
					// Report finished while we were away, remove from list and refresh
					dispatch(removeGeneratingReport(report._id));
					dispatch(getCostReports());
				} else {
					// Still generating, resume polling
					dispatch(pollReportStatus(report._id));
				}
			})
			.catch(function() {
				// Report might not exist anymore, remove from list
				dispatch(removeGeneratingReport(report._id));
			});
	});
};

/**
 * Delete a cost report
 */
export const deleteCostReport = (reportId) => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	
	const request = axios.delete(`${API_BASE_URL}/api/cost-reports/${reportId}`, headers);
	
	return request
		.then(() => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(ActionsDialog.closeDialog());
			dispatch(getCostReports());
			dispatch(
				showMessage({
					message: 'Report deleted successfully',
					variant: 'success'
				})
			);
		})
		.catch(function(err) {
			dispatch(ActionsDialog.toggleLoading());
			var errData = err.response && err.response.data ? err.response.data : { message: 'Failed to delete report' };
			dispatch(setError(errData));
		});
};

/**
 * Export report as PDF
 */
export const exportCostReport = (reportId) => dispatch => {
	dispatch(setLoading(true));
	
	axios.post(
		`${API_BASE_URL}/api/cost-reports/${reportId}/export`,
		{ format: 'pdf' },
		{ ...headers, responseType: 'blob' }
	)
		.then(response => {
			// Create download link
			const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `cost-report-${reportId}.pdf`);
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
			dispatch(setLoading(false));
			dispatch(
				showMessage({
					message: 'PDF exported successfully',
					variant: 'success'
				})
			);
		})
		.catch(err => {
			dispatch(setLoading(false));
			dispatch(
				showMessage({
					message: 'Failed to export report',
					variant: 'error'
				})
			);
		});
};

// ============================================================
// Cleanup Action Creators and Thunks
// ============================================================

/**
 * Start cleanup operation
 */
export const cleanupStart = (totalResources) => ({
	type: CLEANUP_START,
	payload: { total: totalResources }
});

/**
 * Update cleanup progress
 */
export const cleanupProgress = (completed, current) => ({
	type: CLEANUP_PROGRESS,
	payload: { completed: completed, current: current }
});

/**
 * Add cleanup result for a single resource
 */
export const cleanupResourceResult = (result) => ({
	type: CLEANUP_RESOURCE_RESULT,
	payload: result
});

/**
 * Mark cleanup as complete
 */
export const cleanupComplete = () => ({
	type: CLEANUP_COMPLETE
});

/**
 * Reset cleanup state
 */
export const cleanupReset = () => ({
	type: CLEANUP_RESET
});

/**
 * Delete a single resource via API
 * @param {object} options - { provider, resourceType, resourceId, region, credentialId, details, reportId }
 * @returns {Promise} - Resolves with result object
 */
export const deleteResource = (options) => function(dispatch) {
	var provider = options.provider;
	var resourceType = options.resourceType;
	var resourceId = options.resourceId;
	var region = options.region;
	var credentialId = options.credentialId;
	var details = options.details || {};
	var reportId = options.reportId;

	var request = axios.post(
		API_BASE_URL + '/api/cost-reports/cleanup/resource',
		{
			provider: provider,
			resourceType: resourceType,
			resourceId: resourceId,
			region: region,
			credentialId: credentialId,
			details: details,
			reportId: reportId
		},
		headers
	);

	return request
		.then(function(response) {
			return {
				resourceId: resourceId,
				resourceType: resourceType,
				success: true,
				message: response.data.message || 'Deleted successfully'
			};
		})
		.catch(function(error) {
			var errorMessage = 'Failed to delete';
			if (error.response && error.response.data) {
				errorMessage = error.response.data.message || error.response.data.error || errorMessage;
			} else if (error.message) {
				errorMessage = error.message;
			}

			return {
				resourceId: resourceId,
				resourceType: resourceType,
				success: false,
				error: errorMessage
			};
		});
};

/**
 * Cleanup multiple resources sequentially
 * This thunk manages the entire cleanup workflow using Redux actions
 * 
 * @param {object} options - { resources, provider, resourceType, credentialId, reportId }
 */
export const cleanupResources = (options) => function(dispatch) {
	var resources = options.resources || [];
	var provider = options.provider;
	var resourceType = options.resourceType;
	var credentialId = options.credentialId;
	var reportId = options.reportId;

	if (resources.length === 0) {
		return Promise.resolve();
	}

	// Start cleanup
	dispatch(cleanupStart(resources.length));

	// Process resources sequentially
	var index = 0;

	function processNext() {
		if (index >= resources.length) {
			// All done
			dispatch(cleanupComplete());
			
			// Refresh the report to get updated data from database
			if (reportId) {
				dispatch(getCostReport(reportId));
			}
			return;
		}

		var resource = resources[index];

		// Update progress with current resource
		dispatch(cleanupProgress(index, resource));

		// Delete the resource
		dispatch(deleteResource({
			provider: provider,
			resourceType: resource.resourceType || resourceType,
			resourceId: resource.resourceId,
			region: resource.region,
			credentialId: credentialId,
			details: resource.details || {},
			reportId: reportId
		}))
		.then(function(result) {
			// Add result
			dispatch(cleanupResourceResult(result));

			index++;

			// Small delay to avoid overwhelming the API
			setTimeout(processNext, 500);
		});
	}

	// Start processing
	processNext();
};
